import { t } from '@lingui/core/macro';
import { SolutionTag, Tag } from '@luminovo/api-contracts';
import { uniqueBy } from '@luminovo/commons';
import { colorSystem } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';

import { formatNoLongerSelectableReason } from '../../formatters';
import { solutionTagTranslations } from '../../i18n';
import { Solution, SolutionErrorTags, SolutionNotificationTags, SolutionWarningTags } from '../../types';

/** Styled as warnings but, as in the backend, they don't affect the solution status (`extractSolutionStatus`). */
export const WarningStyledTags: readonly SolutionTag[] = [SolutionTag.NoLongerSelectable] as const;

/** Tags shown in the UI, by severity; internal tags such as ManualStatus are left out. */
export const VisibleSolutionTags: readonly SolutionTag[] = [
    ...SolutionErrorTags,
    ...SolutionWarningTags,
    ...WarningStyledTags,
    SolutionTag.Inventory,
    SolutionTag.SupplierPreferred,
    SolutionTag.SupplierApproved,
    ...SolutionNotificationTags,
] as const;

export interface SolutionTagChip {
    color: keyof typeof colorSystem;
    label: string;
    solutionTag: SolutionTag;
    /** For tags whose label alone isn't clear or whose content carries a reason. */
    tooltip?: string;
}

const extractSolutionTagTooltip = (tags: Tag[], solutionTag: SolutionTag): string | undefined => {
    if (solutionTag === SolutionTag.PartAlternative) {
        return t`The supplier offered this part as an alternative to the requested part.`;
    }
    if (solutionTag === SolutionTag.PartAlternativeWarning) {
        return t`The supplier offered this part as an alternative to the requested part. It is flagged as a warning in offer selection.`;
    }
    if (solutionTag === SolutionTag.NoLongerSelectable) {
        // Several reasons collapse into one chip, so its tooltip lists them all.
        return tags
            .flatMap((tag) =>
                tag.tag === SolutionTag.NoLongerSelectable ? [formatNoLongerSelectableReason(tag.content)] : [],
            )
            .join('. ');
    }
    return undefined;
};

const filterAndPrioritiseSolutionTags = (
    tags: Tag[],
    tagKind: readonly SolutionTag[],
    color: keyof typeof colorSystem,
): SolutionTagChip[] => {
    return tags
        .filter((tag) => tagKind.includes(tag.tag))
        .sort((a, b) => (tagKind.indexOf(a.tag) > tagKind.indexOf(b.tag) ? 1 : -1))
        .map((tag) => {
            return {
                color,
                label: transEnum(tag.tag, solutionTagTranslations),
                solutionTag: tag.tag,
                tooltip: extractSolutionTagTooltip(tags, tag.tag),
            };
        });
};

export const extractSolutionTagChips = ({ solutionTags }: Pick<Solution, 'solutionTags'>): SolutionTagChip[] => {
    const violetChips = filterAndPrioritiseSolutionTags(solutionTags, [SolutionTag.PartAlternative], 'violet');
    const blueChips = filterAndPrioritiseSolutionTags(solutionTags, [SolutionTag.Inventory], 'blue');
    const primaryChips = filterAndPrioritiseSolutionTags(solutionTags, [SolutionTag.SupplierPreferred], 'primary');
    const greenChips = filterAndPrioritiseSolutionTags(solutionTags, [SolutionTag.SupplierApproved], 'green');
    const errorChips = filterAndPrioritiseSolutionTags(solutionTags, SolutionErrorTags, 'red');
    const warningChips = filterAndPrioritiseSolutionTags(
        solutionTags,
        [...SolutionWarningTags, ...WarningStyledTags],
        'yellow',
    );
    const notificationChips = filterAndPrioritiseSolutionTags(
        solutionTags,
        SolutionNotificationTags.filter((tag) => tag !== SolutionTag.PartAlternative),
        'neutral',
    );

    return uniqueBy(
        [
            ...violetChips,
            ...blueChips,
            ...primaryChips,
            ...greenChips,
            ...errorChips,
            ...warningChips,
            ...notificationChips,
        ],
        (x) => x.label,
    );
};

export const extractSolutionTagColor = ({ solutionTag }: { solutionTag: SolutionTag }): keyof typeof colorSystem => {
    switch (solutionTag) {
        case SolutionTag.PartAlternative:
            return 'violet';
        case SolutionTag.Inventory:
            return 'blue';
        case SolutionTag.SupplierPreferred:
            return 'primary';
        case SolutionTag.SupplierApproved:
            return 'green';
        default:
            if (SolutionErrorTags.includes(solutionTag)) {
                return 'red';
            }
            if (SolutionWarningTags.includes(solutionTag) || WarningStyledTags.includes(solutionTag)) {
                return 'yellow';
            }
            if (SolutionNotificationTags.includes(solutionTag)) {
                return 'neutral';
            }
            return 'neutral';
    }
};

export interface SolutionTagCounts {
    errorCount: number;
    warningCount: number;
    infoCount: number;
    errorTags: SolutionTagChip[];
    warningTags: SolutionTagChip[];
    infoTags: SolutionTagChip[];
}

export const groupSolutionTagsBySeverity = (tags: Tag[]): SolutionTagCounts => {
    const chips = extractSolutionTagChips({ solutionTags: tags });
    const errorTags = chips.filter((c) => SolutionErrorTags.includes(c.solutionTag));
    const warningTags = chips.filter(
        (c) => SolutionWarningTags.includes(c.solutionTag) || WarningStyledTags.includes(c.solutionTag),
    );
    const infoTags = chips.filter(
        (c) =>
            !SolutionErrorTags.includes(c.solutionTag) &&
            !SolutionWarningTags.includes(c.solutionTag) &&
            !WarningStyledTags.includes(c.solutionTag),
    );

    return {
        errorCount: errorTags.length,
        warningCount: warningTags.length,
        infoCount: infoTags.length,
        errorTags,
        warningTags,
        infoTags,
    };
};
